/**
 * The Nest tab (docs/NESTS.md, docs/mockups/nest-prototype.html round 3).
 *
 * - No nest yet: the app's empty state, with Nib, one line, Start a nest and Join with a code.
 * - In a nest: its name is the title and the switcher (one sheet: your nests, Invite, Start,
 *   Join, then Mute and Leave). Under it, Today · This week · All time:
 *   - Today (Nest boards v2, docs/nest-boards/contract.md): three level cards, then the
 *     chosen level's ranked list (nests/model.ts orders it): times, "N reveals", a status
 *     and Nudge for who hasn't finished, and your row on top with "Play {Level}" until
 *     you've played that level. Opens on the last level you finished today, else Easy.
 *     Activity below, when there's any today.
 *   - This week / All time: a header ("Ends Sun", Feathers ⓘ), the ranked list with
 *     your row in place, the week's strips (with a key) or All time's labels.
 *
 * Shown behind the nests gate (nests/store.ts). Everything is fetched fresh (the api says
 * no-store) and kept in memory for the visit, so switching tabs doesn't flash empty.
 * All dates sent are the device's own local date.
 */
import { currentStreak, type DateKey } from '@arrow/game-engine';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { setPageTitle } from '../app/page-title.ts';
import { AppMenu } from '../components/AppMenu.tsx';
import { BrandRow } from '../components/BrandRow.tsx';
import { Nib } from '../components/Nib.tsx';
import { ErrorState, Pending, SkeletonRows } from '../components/States.tsx';
import { TabBar } from '../components/TabBar.tsx';
import {
	type ActivityItem,
	fetchActivity,
	fetchAllTime,
	fetchFeathers,
	fetchMyNests,
	fetchNest,
	fetchToday,
	fetchWeek,
	type FeatherBoard,
	type FeathersInfo,
	leaveNest,
	NestError,
	type NestSummary,
	type NestView,
	sendNudge,
	type TodayBoard,
	toggleWellDone,
	updateMe,
} from '../nests/api.ts';
import {
	featherRows,
	levelCards,
	levelRows,
	type LevelRow,
	namesFor,
	nudgedOn,
	nudgeSentLine,
	openingLevel,
	shortName,
	todaysActivity,
	weekEndsTag,
} from '../nests/model.ts';
import type { Level } from '@arrow/puzzle-schema';
import { currentNest, forgetNest, rememberNest, rememberNests, setCurrentNest, takeJoined, useNestMemory } from '../nests/store.ts';
import { retryPendingSolves } from '../platform/sync.ts';
import { NestJoinMoment, useNudgeMoment } from '../push/Moments.tsx';
import { noteNestJoined } from '../push/push.ts';
import { hasSolved } from '../push/state.ts';
import { type Persistence, slotFor } from '../storage/persistence.ts';
import { prefs } from '../app/prefs.ts';
import { Tabs, TabsContent, TabsList, TabsTrigger, useSheetOpeners, useToast } from '../ui/index.ts';
import { ActivityFeed, FeatherList, FeathersLabel, LevelList, LevelTabs, type PlayLink, WeekLegend } from './nest/Board.tsx';
import { Chevron, nestErrorLine } from './nest/parts.tsx';
import { BringSheet, InviteSheet, JoinSheet, MoveSheet, NestMenuSheet, type NestSheetKind, StartSheet } from './nest/Sheets.tsx';
import { readPlatform } from '../pwa/platform.ts';
import '../styles/nav.css';
import './nest/nest.css';

type View = 'today' | 'week' | 'all';
type Load<T> = { status: 'loading' } | { status: 'error'; error: unknown } | { status: 'ready'; value: T };

/** What the visit has already fetched, by nest and date: shown at once, then refreshed. */
const memo = new Map<string, unknown>();
/** Tests: forget the visit's fetches. */
export function resetNestMemo(): void {
	memo.clear();
}

/** A fetch kept in `memo`: the last value shows while a fresh one loads (and on a failed refresh). */
function useFetched<T>(key: string | null, load: (signal: AbortSignal) => Promise<T>, refresh: number): [Load<T>, (next: T) => void] {
	const [state, setState] = useState<Load<T>>(() => (key && memo.has(key) ? { status: 'ready', value: memo.get(key) as T } : { status: 'loading' }));
	const loadRef = useRef(load);
	loadRef.current = load;
	useEffect(() => {
		if (!key) return;
		const ctrl = new AbortController();
		const had = memo.has(key);
		setState(had ? { status: 'ready', value: memo.get(key) as T } : { status: 'loading' });
		loadRef.current(ctrl.signal).then(
			(value) => {
				if (ctrl.signal.aborted) return;
				memo.set(key, value);
				setState({ status: 'ready', value });
			},
			(error: unknown) => {
				if (ctrl.signal.aborted) return;
				if (!memo.has(key)) setState({ status: 'error', error });
			},
		);
		return () => ctrl.abort();
	}, [key, refresh]);
	const set = useCallback(
		(value: T) => {
			if (key) memo.set(key, value);
			setState({ status: 'ready', value });
		},
		[key],
	);
	return [state, set];
}

/** The nest and view a notification opened (`?n=`, `?view=`), read once and taken out of the address. */
let linked: { nest: string | null; view: string | null } | null = null;
function linkedNest(): { nest: string | null; view: string | null } {
	if (linked) return linked;
	linked = { nest: null, view: null };
	try {
		const url = new URL(window.location.href);
		linked = { nest: url.searchParams.get('n'), view: url.searchParams.get('view') };
		if (linked.nest || linked.view) {
			for (const k of ['n', 'view']) url.searchParams.delete(k);
			const q = url.searchParams.toString();
			window.history.replaceState(window.history.state, '', `${url.pathname}${q ? `?${q}` : ''}${url.hash}`);
		}
		if (linked.nest) setCurrentNest(linked.nest);
	} catch {
		// Just the nest tab, as it was.
	}
	return linked;
}

/** Tests: forget the link read on the first visit. */
export function resetNestLink(): void {
	linked = null;
}

export function NestScreen({ todayKey, persistence }: { todayKey: DateKey; persistence: Persistence }) {
	useState(linkedNest);
	const memory = useNestMemory();
	const known = currentNest(memory);
	const streak = useMemo(() => currentStreak(persistence.loadStats(), todayKey), [persistence, todayKey]);
	// The notification moments wait for the first solve (push/state.ts).
	const solvedBefore = useMemo(() => hasSolved(persistence.loadStats()), [persistence]);
	const nudgeMoment = useNudgeMoment({ solvedBefore });
	// A slip above the tab bar, over a sheet when one raised it ("Link copied").
	const toast = useToast({ overSheet: true, testId: 'nest-toast' });
	const [sheet, setSheet] = useState<NestSheetKind | null>(null);
	const closeSheet = useCallback(() => setSheet(null), []);
	// Each sheet takes the phone's back itself (ui/Sheet); one handing over to the next keeps the one entry.
	// A notification's link (`/nest?n=<nest>&view=week&source=push…`): that nest, that view, once.
	const [view, setView] = useState<View>(() => (linkedNest().view === 'week' ? 'week' : 'today'));
	// An iPhone keeps Safari's storage and the home-screen app's apart: each side gets its half of the move.
	const [platform] = useState(readPlatform);
	const iphoneTab = platform.ios && !platform.standalone && !platform.inApp;
	const iphoneApp = platform.ios && platform.standalone;
	const [fx, setFx] = useState(false);
	// Today's level, once you pick one; until then the page opens on the last level you finished.
	const [level, setLevel] = useState<Level | null>(null);
	// Bumped to fetch again: coming back to the page, a pull after a change.
	const [refresh, setRefresh] = useState(0);
	const again = useCallback(() => setRefresh((n) => n + 1), []);

	useEffect(() => {
		setPageTitle('Nest');
		// Just joined from an invite with every level already solved: the invite landed here (QA2-13).
		const joined = takeJoined();
		if (joined) toast.show(`You’re in ${joined}`);
		// Once, on arrival.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fresh when the page comes back into view, and every minute while it's open.
	useEffect(() => {
		const onVisible = () => {
			if (document.visibilityState === 'visible') again();
		};
		document.addEventListener('visibilitychange', onVisible);
		const timer = window.setInterval(() => {
			if (document.visibilityState === 'visible') again();
		}, 60_000);
		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.clearInterval(timer);
		};
	}, [again]);

	// The device's nests, from the api (a nest left or removed elsewhere drops out).
	const [list] = useFetched<NestSummary[]>('list', (s) => fetchMyNests(s), refresh);
	useEffect(() => {
		if (list.status === 'ready') rememberNests(list.value);
	}, [list]);
	// The device's memory leads (it keeps a nest just joined that the api's list hasn't caught
	// up with, NJ-1); the list adds the head-counts.
	const nests = memory.nests.map(
		(n): NestSummary => (list.status === 'ready' ? list.value.find((x) => x.id === n.id) : undefined) ?? { id: n.id, name: n.name, memberCount: 0, role: 'member', muted: false },
	);

	const id = known?.id ?? null;
	const [nestLoad, setNest] = useFetched<NestView>(id && `nest:${id}`, (s) => fetchNest(id!, s), refresh);
	const nest = nestLoad.status === 'ready' ? nestLoad.value : null;
	// A nest that's gone (left on another device, removed): forget it.
	useEffect(() => {
		if (id && nestLoad.status === 'error' && nestLoad.error instanceof NestError && nestLoad.error.kind === 'not-found') forgetNest(id);
	}, [id, nestLoad]);

	const solo = nest !== null && nest.members.length <= 1;
	// Today's board even when it's just you: its number heads the page.
	const [today] = useFetched<TodayBoard>(id && nest ? `today:${id}:${todayKey}` : null, (s) => fetchToday(id!, todayKey, s), refresh);
	const [week] = useFetched<FeatherBoard>(id && !solo && view === 'week' ? `week:${id}:${todayKey}` : null, (s) => fetchWeek(id!, todayKey, s), refresh);
	const [all] = useFetched<FeatherBoard>(id && !solo && view === 'all' ? `all:${id}:${todayKey}` : null, (s) => fetchAllTime(id!, todayKey, s), refresh);
	const [feed, setFeed] = useFetched<ActivityItem[]>(id && !solo ? `feed:${id}` : null, (s) => fetchActivity(id!, s), refresh);
	const [feathers] = useFetched<FeathersInfo>(view === 'today' ? null : 'feathers', (s) => fetchFeathers(s), 0);

	// Nudges sent this visit (the feed knows the earlier ones).
	const [sent, setSent] = useState<Set<string>>(() => new Set());
	const todayBoard = today.status === 'ready' ? today.value : null;
	const nudged = useMemo(() => {
		const out = new Set(sent);
		if (todayBoard && feed.status === 'ready') for (const m of nudgedOn(feed.value, todayBoard.number, todayBoard.me)) out.add(m);
		return out;
	}, [sent, todayBoard, feed]);

	const myName = memory.name ?? '';
	const afterJoin = (joined: NestView, me: string) => {
		// The new nest is current and stays current through the refresh that follows, even if
		// a list fetched before the join comes back first (NJ-1: nests/store.ts keeps it).
		rememberNest({ id: joined.id, name: joined.name }, me);
		memo.set(`nest:${joined.id}`, joined);
		// Solves held back by "Count my plays" can go now: the nest sees them.
		void retryPendingSolves();
		setView('today');
		again();
	};

	const onNudge = async (row: LevelRow) => {
		if (!nest) return;
		try {
			await sendNudge(nest.id, { targetMemberId: row.memberId, phrase: 'your_go', date: todayKey });
			setSent((s) => new Set(s).add(row.memberId));
			// Notifications off here: the toast offers them ("{name} can nudge you back").
			const among = nest.members.map((m) => m.displayName);
			if (!nudgeMoment.offer(shortName(row.name, among))) toast.show(nudgeSentLine(row.name, nest.me.displayName, among));
			again();
		} catch (err) {
			const msg = err instanceof NestError ? err.message.toLowerCase() : '';
			if (msg.includes('already nudged')) {
				setSent((s) => new Set(s).add(row.memberId));
				toast.show(`You’ve nudged ${row.name} today`);
			} else if (msg.includes('already played')) {
				toast.show(`${row.name} has just played`);
				again();
			} else toast.show('Couldn’t send the nudge. Try again.');
		}
	};

	const onWellDone = async (item: ActivityItem) => {
		if (!nest || feed.status !== 'ready') return;
		const flip = (mine: boolean, count: number) =>
			setFeed(feed.value.map((i) => (i.id === item.id ? { ...i, wellDone: { mine, count } } : i)));
		const before = item.wellDone;
		flip(!before.mine, Math.max(0, before.count + (before.mine ? -1 : 1)));
		try {
			const res = await toggleWellDone(nest.id, item.id);
			flip(res.mine, res.count);
		} catch {
			flip(before.mine, before.count);
			toast.show('Couldn’t send your well done. Try again.');
		}
	};

	const onMute = async (muted: boolean) => {
		if (!nest) return;
		setNest({ ...nest, me: { ...nest.me, muted } });
		try {
			setNest(await updateMe(nest.id, { muted }));
		} catch {
			setNest(nest);
			toast.show(`Couldn’t ${muted ? 'mute' : 'unmute'} ${nest.name}. Try again.`);
		}
	};

	// The Nest tab stays whether or not you're in a nest, so leaving never hides it.
	const onLeave = async (): Promise<string | null> => {
		if (!nest) return null;
		try {
			await leaveNest(nest.id);
		} catch (err) {
			return nestErrorLine(err, 'save');
		}
		forgetNest(nest.id);
		memo.delete(`nest:${nest.id}`);
		setSheet(null);
		toast.show(`You’ve left ${nest.name}`);
		again();
		return null;
	};

	// The open sheet. A button that opens one is its Radix trigger (`opens.trigger`), and the
	// sheet opens inside that button's root; one opened from code or from a row in another
	// sheet (menu → Invite, Join → Start) is drawn by `opens.rest`.
	const sheetNode = () => (
		<>
			{sheet === 'start' && (
				<StartSheet
					todayKey={todayKey}
					myName={myName}
					onClose={closeSheet}
					onStarted={(n, me) => {
						afterJoin(n, me);
						noteNestJoined(n.name);
						setSheet('invite');
						toast.show(`${n.name} is ready`);
					}}
				/>
			)}
			{sheet === 'join' && (
				<JoinSheet
					todayKey={todayKey}
					myName={myName}
					onClose={closeSheet}
					onJoined={(n, me) => {
						afterJoin(n, me);
						noteNestJoined(n.name);
						setSheet(null);
						toast.show(`You’re in ${n.name}`);
					}}
					onAlreadyIn={() => {
						setSheet(null);
						again();
					}}
					onStart={() => setSheet('start')}
				/>
			)}
			{sheet === 'invite' && nest && <InviteSheet nest={nest} onClose={closeSheet} />}
			{sheet === 'move' && <MoveSheet onClose={closeSheet} />}
			{sheet === 'bring' && (
				<BringSheet
					onClose={closeSheet}
					onBrought={() => {
						// The device id changed: start the app again as that device, on the Nest tab.
						resetNestMemo();
						window.location.assign('/nest');
					}}
				/>
			)}
			{sheet === 'menu' && nest && (
				<NestMenuSheet
					nest={nest}
					nests={nests}
					onClose={closeSheet}
					onSwitch={(to) => {
						setCurrentNest(to);
						setSheet(null);
						setView('today');
						setLevel(null);
						setFx(false);
					}}
					onPick={setSheet}
					onMute={(m) => void onMute(m)}
					onLeave={onLeave}
					canMove={iphoneTab}
					canBring={iphoneApp}
				/>
			)}
		</>
	);
	const opens = useSheetOpeners(sheet, sheetNode);

	const shell = (body: ReactNode, title: ReactNode = <h1 className="page-title">Nest</h1>) => (
		<div className="page nest">
			{/* The brand row is the page's banner, outside <main> (QA4-13); both scroll together. */}
			<div className="page-body page-scroll">
				<BrandRow streak={streak} after={<AppMenu screen="nest" install={{ todayKey }} />} />
				<main className="page-main">
					{/* The title and its rule span the shell, as on every tab; the nest's column is capped, on the left. */}
					{title}
					<div className="nest-col">{body}</div>
				</main>
			</div>
			<TabBar current="nest" />
			{opens.rest}
			{toast.node}
			{nudgeMoment.node}
		</div>
	);

	// No nest yet.
	if (!known) {
		return shell(
			<div className="empty nest-empty" data-testid="nest-empty">
				<Nib pose="lookup" className="empty-nib" />
				<p className="empty-line">Compare times with family and friends.</p>
				<p className="empty-sub">A nest is a private group of up to 30 people. You see each other’s times on the day’s puzzles. Free, no sign-up.</p>
				{opens.trigger(
					'start',
					<button type="button" className="btn lg primary fit" onClick={() => setSheet('start')}>
						Start a nest
					</button>,
				)}
				{opens.trigger(
					'join',
					<button type="button" className="tlink" onClick={() => setSheet('join')}>
						Join with a code
					</button>,
				)}
				{iphoneApp &&
					opens.trigger(
						'bring',
						<button type="button" className="tlink" onClick={() => setSheet('bring')} data-testid="nest-bring-open">
							Joined in Safari? Bring your nests here
						</button>,
					)}
			</div>,
		);
	}

	const name = nest?.name ?? known.name;
	const many = nests.length > 1;
	// The h1 is the nest's name (and the switcher); a long name steps down and takes two lines.
	const title = (
		// The heading is the name alone (aria-labelledby); the button says what it does (QA4-13).
		<h1 className="page-title nest-title" data-long={[...name].length > 14 || undefined} aria-labelledby="nest-title-name">
			{opens.trigger(
				'menu',
				<button type="button" className="nest-pick" onClick={() => setSheet('menu')} disabled={!nest}>
					<span id="nest-title-name">{name}</span>
					<span className="sr-only">{many ? ', switch nest' : ', options'}</span>
					<Chevron />
				</button>,
			)}
		</h1>
	);
	const waiting = (
		<Pending label="Loading">
			<SkeletonRows count={5} />
		</Pending>
	);

	if (nestLoad.status === 'loading') return shell(waiting, title);
	if (nestLoad.status === 'error' || !nest) {
		return shell(<LoadError onRetry={again} />, title);
	}

	if (solo) {
		return shell(
			<div className="empty nest-empty" data-testid="nest-solo">
				<p className="empty-line">Nobody else yet.</p>
				<p className="empty-sub">Invite people and their times show up here.</p>
				{opens.trigger(
					'invite',
					<button type="button" className="btn lg primary fit" onClick={() => setSheet('invite')}>
						Invite people
					</button>,
				)}
			</div>,
			title,
		);
	}

	const show = (k: View) => {
		if (k === view) return;
		setView(k);
		setFx(false);
	};
	const views: readonly { value: View; label: string }[] = [
		{ value: 'today', label: 'Today' },
		{ value: 'week', label: 'This week' },
		{ value: 'all', label: 'All time' },
	];

	let body: ReactNode;
	if (view === 'today') {
		if (today.status === 'loading') body = waiting;
		else if (today.status === 'error') body = <LoadError onRetry={again} what="today’s times" />;
		else {
			const board = today.value;
			// This device's own finishes count too (the nest may not have heard yet).
			const doneHere = (l: Level) => persistence.resumeInfo(slotFor({ kind: 'daily', date: board.date, level: l })).status === 'completed';
			const shown = level && board.levels.some((l) => l.level === level) ? level : openingLevel(board, prefs().lastLevel(), doneHere);
			const rows = levelRows(board, nest.members, shown);
			// Your row's way in to this level, unless this device has already solved it.
			const play: PlayLink | null = doneHere(shown) ? null : { level: shown, to: `/daily/${board.number}?l=${shown}` };
			body = (
				<>
					{/* While notifications aren't on: know when this nest plays (push/Moments.tsx). */}
					<NestJoinMoment solvedBefore={solvedBefore} nestName={nest.name} />
					<LevelTabs cards={levelCards(board, nest.members)} value={shown} onChange={setLevel}>
						<LevelList level={shown} rows={rows} nudged={nudged} onNudge={(r) => void onNudge(r)} play={play} />
					</LevelTabs>
					<ActivityFeed
						items={feed.status === 'ready' ? todaysActivity(feed.value, board.number, todayKey) : []}
						status={feed.status}
						nameOf={namesFor(nest.members, nest.me.memberId)}
						me={nest.me.memberId}
						onWellDone={(i) => void onWellDone(i)}
					/>
				</>
			);
		}
	} else {
		const load = view === 'week' ? week : all;
		const info = feathers.status === 'ready' ? feathers.value : load.status === 'ready' ? load.value.feathers : null;
		const label = view === 'week' ? 'This week, in feathers' : 'All time, in feathers';
		const rows = load.status === 'ready' ? featherRows(load.value, nest.members, todayKey) : [];
		body =
			load.status === 'loading' ? (
				waiting
			) : load.status === 'error' ? (
				<LoadError onRetry={again} what="the feathers" />
			) : (
				<>
					<FeatherList
						rows={rows}
						label={label}
						todayKey={todayKey}
						tag={view === 'week' ? weekEndsTag(todayKey) : null}
						header={<FeathersLabel info={info} open={fx} onToggle={setFx} />}
					/>
					{rows.some((r) => r.week) && <WeekLegend />}
				</>
			);
	}

	// The views (ui/Tabs, printed as the segmented control): the chosen one's panel is the body.
	return shell(
		<Tabs className="nest-flat" value={view} onValueChange={(k) => show(k as View)}>
			<TabsList className="seg nest-seg" aria-label="Show">
				{views.map((v) => (
					<TabsTrigger key={v.value} value={v.value}>
						{v.label}
					</TabsTrigger>
				))}
			</TabsList>
			<TabsContent value={view} className="nest-view">
				{body}
			</TabsContent>
		</Tabs>,
		title,
	);
}

/** A load that failed, in the tab's content slot: the brand row, ≡ and the tab bar stay (UX-016). */
function LoadError({ onRetry, what = 'your nest' }: { onRetry: () => void; what?: string }) {
	return <ErrorState inPage error={{ kind: 'network', message: '' }} what={what} onRetry={onRetry} screen="nest" />;
}
